import React, { useState } from 'react';
import { ModuleBase, HeadingTag, Button } from '@/components';
import Plus from '@/assets/plus.svg';
import Minus from '@/assets/minus.svg';
import classnames from 'classnames';
import { isColorDark, themeFromBg } from '@/utils';
import classNames from 'classnames';
// import { isIOS, isSafari } from 'react-device-detect';
import FadeUpAnimation from '@/components/animations/FadeUpAnimation/FadeUpAnimation';
import FadeUpStarter from '@/components/animations/FadeUpAnimation/FadeUpStarter';
import { motion, AnimatePresence } from 'framer-motion';

const AccordionModule = ({ data }) => {
  const { headingTitle, allowOpeningMultiple, accordionElements, primaryCTA, backgroundColour } = data || {};
  const [activeIndexList, setActiveIndexList] = useState([]);

  const toggle = (index) => {
    let newArray = [...activeIndexList];
    if (newArray.includes(index)) {
      newArray = newArray.filter((i) => i !== index);
    } else {
      if (allowOpeningMultiple) {
        newArray = [index, ...newArray];
      } else {
        newArray = [index];
      }
    }
    setActiveIndexList(newArray);
  };

  const faqData = () => accordionElements;

  return (
    <ModuleBase data={data}>
      <FadeUpStarter className="container lg:pl-[186px] lg:pr-[156px]">
        {headingTitle && (
          <FadeUpAnimation>
            <HeadingTag
              data={headingTitle}
              className="font-ivy-ora text-center text-[56px] font-normal leading-[60px] -tracking-normal color-from-bg lg:text-[80px] lg:leading-[72px] lg:-tracking-ultraWide"
            />
          </FadeUpAnimation>
        )}

        {accordionElements && (
          <div className="mt-6 lg:mt-8">
            {faqData().map((element, index) => (
              <FadeUpAnimation key={index} className="first:pt-0 first:md:pt-4">
                <button
                  type="button"
                  className={`relative pt-6 flex items-start justify-between w-full text-left font-montserrat text-base leading-6.5  lg:pl-[86px] transition-all duration-500 ${activeIndexList.includes(index) ? 'opacity-1' : 'opacity-50'}`}
                  onClick={() => toggle(index)}
                >
                  <div
                    className={`flex items-center justify-center w-[32px] h-[32px] mr-3 lg:absolute lg:left-0  transform-none`}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      {activeIndexList.includes(index) ? (
                        <motion.div
                          key="minus"
                          initial={{ rotate: -90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: 90, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Minus className="w-[16px] h-[16px] lg:w-[19px] lg:h-[19px] color-from-bg" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="plus"
                          initial={{ rotate: 90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: -90, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Plus className="w-[16px] h-[16px] lg:w-[19px] lg:h-[19px] color-from-bg" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className={`flex justify-between items-center w-full transition-all duration-500 }`}>
                    <span
                      className={classnames(
                        activeIndexList.includes(index) ? '' : 'delay-300',
                        'transition-all duration-100',
                        'color-from-bg font-ivy-ora text-[24px] font-normal leading-8 -tracking-wider',
                      )}
                    >
                      {element.title}
                    </span>

                    <div>
                      <Button
                        theme={themeFromBg(isColorDark(backgroundColour))}
                        link={element.link}
                        type="tertiary"
                        className={`hidden lg:block transition-all duration-500 ${
                          activeIndexList.includes(index) ? 'opacity-1' : 'opacity-0'
                        }`}
                      />
                    </div>
                  </div>
                </button>

                <div
                  className={classNames(
                    activeIndexList.includes(index) ? 'h-0' : 'h-10',
                    'helper transition-all duration-400',
                  )}
                ></div>
                <div
                  className={classNames(
                    'h-[1px] transition-all duration-1000 ml-[47px] lg:ml-[86px]',
                    activeIndexList.includes(index) ? 'opacity-0' : 'opacity-1',
                    isColorDark(backgroundColour) ? 'bg-white/10' : 'bg-black/10',
                  )}
                />

                <AnimatePresence initial={false}>
                  {activeIndexList.includes(index) && (
                    <motion.div
                      key="content"
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      variants={{
                        open: { height: 'auto', opacity: 1 },
                        collapsed: { height: 0, opacity: 0 },
                      }}
                      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden ml-[47px] lg:ml-[86px]"
                    >
                      <div
                        className={classNames(
                          activeIndexList.includes(index) ? 'h-6' : 'h-10',
                          'helper transition-all duration-400',
                        )}
                      ></div>
                      {element.text && (
                        <div
                          dangerouslySetInnerHTML={{ __html: element.text }}
                          className="font-montserrat mb-10 color-from-bg text-sm leading-6 font-normal [&_a]:underline [&_a]:underline-offset-4"
                        />
                      )}
                      <div>
                        <Button
                          theme={themeFromBg(isColorDark(backgroundColour))}
                          link={element.link}
                          type="tertiary"
                          className="block lg:hidden w-fit mb-8"
                        />
                      </div>
                      <div
                        className={classNames(
                          'h-[1px] transition-all duration-1000',
                          isColorDark(backgroundColour) ? 'bg-white/10' : 'bg-black/10',
                        )}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </FadeUpAnimation>
            ))}
          </div>
        )}

        {primaryCTA && (
          <FadeUpAnimation className="flex justify-center mt-10">
            <div className="group">
              <Button theme={themeFromBg(isColorDark(backgroundColour))} link={primaryCTA} type={'primary'} />
            </div>
          </FadeUpAnimation>
        )}
      </FadeUpStarter>
    </ModuleBase>
  );
};

export default AccordionModule;
